import React, { useEffect, useMemo, useState } from "react";
import { encryptId } from "../../utils/encryption"; 

import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

const SponsorsOverview = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedUsers, setSelectedUsers] = useState({});
  const [checkAll, setCheckAll] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users`);
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAll = () => {
    const newValue = !checkAll;
    setCheckAll(newValue);
    const newSelected = {};
    if (newValue) {
      users.forEach((_, idx) => {
        newSelected[idx] = true;
      });
    }
    setSelectedUsers(newSelected);
  };

  const handleCheckboxChange = (rowIndex) => {
    setSelectedUsers((prev) => ({ ...prev, [rowIndex]: !prev[rowIndex] }));
  };

  const handleSendEmail = async (toAll = false) => {
    const selected = toAll
      ? users
      : users.filter((_, idx) => selectedUsers[idx]);

    const payload = {
      type: "sponsor",
      lang: "de", // or "en"
      emails: selected.map(({ email, name }) => ({
        email,
        name,
        customMessage: `Dear ${name}, this is a test email for sponsors.`,
      })),
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/email/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Success: ${data.message}`);
      } else {
        alert(`Failed: ${data.message}`);
      }
    } catch (err) {
      console.error("Bulk email error:", err);
      alert("An error occurred while sending sponsor emails.");
    }
  };

  const handleDeleteEntity = async (entityType, entity, fetchEntities) => {
    console.log(entity);
    if (entity.is_special === 1) {
        alert("You cannot remove this account because it is marked as a special sponsor.");
        return;
      }
    const entityId = entity.id;    
    const confirmed = window.confirm(`Are you sure you want to delete this ${entityType}? This action cannot be undone.`);
    if (!confirmed) return;

    const encryptedId = encodeURIComponent(encryptId(entityId));    
    try {
      console.log("Encoded URL:", `${process.env.REACT_APP_API_BASE_URL}/api/${entityType}s/${encryptedId}`);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/${entityType}s/${encryptedId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        alert(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} deleted successfully.`);
        fetchEntities(); // refresh list
      } else {
        const data = await response.json();
        alert(`Failed to delete ${entityType}: ${data.message || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert(`An error occurred while deleting the ${entityType}.`);
    }
  };


  const columns = useMemo(
    () => [
      {
        id: "select",
        header: () => (
          <input type="checkbox" checked={checkAll} onChange={handleCheckAll} />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={!!selectedUsers[row.index]}
            onChange={() => handleCheckboxChange(row.index)}
          />
        ),
      },
      { header: "Name", accessorKey: "name" },
      { header: "Email", accessorKey: "email" },
      {
        header: "Company",
        accessorKey: "company",
        cell: (info) => {
          const value = info.getValue();
          return value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
        },
      },
      {
        header: "Product",
        accessorKey: "sponsorproduct",
        cell: (info) => {
          const value = info.getValue();
          return value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
        },
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      },
      {
        header: "Actions",
        cell: ({ row }) => {
          const club = row.original;
          return (
            <button
              className="text-red-600 hover:text-red-800"
              onClick={() => handleDeleteEntity("user", club, fetchUsers)}
              title="Delete"
            >
              🗑️
            </button>
          );
        },
      },

    ],
    [selectedUsers, checkAll]
  );

  const table = useReactTable({
    data: users,
    columns,
    state: {
      globalFilter,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Sponsors Overview</h1>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search..."
              className="border p-2 rounded w-full sm:w-1/2"
              value={globalFilter ?? ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </div>

          <div className="flex gap-4 mb-4">
            <button onClick={() => handleSendEmail(true)} className="px-4 py-2 bg-blue-600 text-white rounded">
              Send Email to All
            </button>
            <button onClick={() => handleSendEmail(false)} className="px-4 py-2 bg-green-600 text-white rounded">
              Send Email to Selected
            </button>
          </div>

          <div className="overflow-x-auto border rounded">
            <table className="min-w-full table-auto text-sm text-left text-gray-700">
              <thead className="bg-gray-100">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler?.()}
                        className="p-3 font-semibold cursor-pointer select-none text-left"
                      >
                        <div className="flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: "▲",
                            desc: "▼",
                          }[header.column.getIsSorted?.()] ?? ""}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-t hover:bg-gray-100 even:bg-gray-50">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="p-3 text-left">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
              <span>
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
            </div>

            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              className="border p-1 rounded"
            >
              {[5, 10, 20, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
    </div>
  );
};

export default SponsorsOverview;
